import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, Subscription, interval } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ExtendedBusinessPartner, BusinessPartner, FilterState, Evaluation, SharedInformation, Estadisticas } from '../../_models/business-partner.model';
import { Configuration } from '../../_models/configuration.model';
import { SnackBarService } from '../../components/snack-bar/snackBar.service';
import { GtmService } from '../gtm/gtm.service';
import { CompanyInformationRisk, CompanyInformationRiskLastMonths, RiskRequest } from '../../_models/company.model';

/**
 * Servicio para gestión de Socios de Negocio
 * Comunica con el BFF para operaciones CRUD
 */
@Injectable({
  providedIn: 'root',
})
export class BusinessPartnerService {
  private apiUrl = `${environment.urlApi}/socios-negocio`;

  // Subject para compartir la lista de socios
  private partnersSubject = new BehaviorSubject<ExtendedBusinessPartner[]>([]);
  partners$ = this.partnersSubject.asObservable();

  // Subject para estado de carga global
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  // Subject para socio seleccionado (similar a CompanyService)
  private selectedPartnerSubject = new BehaviorSubject<ExtendedBusinessPartner | null>(null);
  selectedPartner$ = this.selectedPartnerSubject.asObservable();

  // Subject para configuración seleccionada
  private selectedConfigurationSubject = new BehaviorSubject<Configuration | null>(null);
  selectedConfiguration$ = this.selectedConfigurationSubject.asObservable();

  // Subject para el estado del polling
    private riskPollingSubject = new BehaviorSubject<{
      status: number;
      message: string;
    } | null>(null);
    riskPolling$ = this.riskPollingSubject.asObservable();
  
    private pollingSubscription?: Subscription;
    private readonly POLLING_INTERVAL = 3000; // 3 segundos

  constructor(
    private http: HttpClient,
    private snackBarService: SnackBarService,
    private gtmService: GtmService
  ) {}

  /**
   * Obtiene todos los socios de negocio
   * @param companyId ID de la empresa
   * @param usuarioId ID del usuario
   * @param page Página a mostrar
   * @param len Cantidad de elementos por página
   * @param filters Filtros opcionales a aplicar
   * @returns Observable con array de socios
   */
  getAllPartners(companyId: string, usuarioId: string, page: number = 1, len: number = 10, filters?: FilterState): Observable<ExtendedBusinessPartner[]> {
    this.loadingSubject.next(true);

    let params = new HttpParams()
      .set('usuarioId', usuarioId)
      .set('page', page.toString())
      .set('len', len.toString());

    if (filters) {
      if (filters.classification.length) params = params.set('clasificaciones', filters.classification.join(','));
      if (filters.tags.length) params = params.set('tags', filters.tags.join(','));
      if (filters.gestCenter.length) params = params.set('centros', filters.gestCenter.join(','));
      if (filters.productsServices.length) params = params.set('productos', filters.productsServices.join(','));
      if (filters.status.length) params = params.set('estado', filters.status.join(','));
      if (filters.risk.length) params = params.set('riesgo', filters.risk.join(','));
      if (filters.flags.length) params = params.set('banderas', filters.flags.join(','));
      if (filters.lastInvoiceDateFrom) params = params.set('fInicio', filters.lastInvoiceDateFrom);
      if (filters.lastInvoiceDateTo) params = params.set('fFin', filters.lastInvoiceDateTo);
      if (filters.coverage.length) params = params.set('cobertura', filters.coverage);
      if (filters.searchTerm.length) params = params.set('terms', filters.searchTerm);
    }

    return this.http.get<ExtendedBusinessPartner[]>(`${this.apiUrl}/search/${companyId}`, {
      params,
      observe: 'response'
    }).pipe(
      tap((response) => {
        const partners = response.body || [];
        this.partnersSubject.next(partners);
        this.loadingSubject.next(false);

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'listar',
          componente_ui: 'business_partners_service',
          valor: partners.length,
          resultado_accion: 'exitoso'
        });
      }),
      map((response) => response.body || []),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'listar');
      })
    );
  }

  /**
    * Obtiene todos los socios de negocio con información de paginación
    * @param companyId ID de la empresa
    * @param usuarioId ID del usuario
    * @param page Página a mostrar
    * @param len Cantidad de elementos por página
    * @param filters Filtros opcionales a aplicar
    * @returns Observable con datos y total de registros
    */
  getAllPartnersPaginated(companyId: string, usuarioId: string, page: number = 1, len: number = 10, filters?: FilterState): Observable<{partners: ExtendedBusinessPartner[], total: number}> {
    this.loadingSubject.next(true);

    let params = new HttpParams()
      .set('usuarioId', usuarioId)
      .set('page', page.toString())
      .set('len', len.toString());

    if (filters) {
      if (filters.classification.length) params = params.set('clasificaciones', filters.classification.join(','));
      if (filters.tags.length) params = params.set('tags', filters.tags.join(','));
      if (filters.gestCenter.length) params = params.set('centros', filters.gestCenter.join(','));
      if (filters.productsServices.length) params = params.set('productos', filters.productsServices.join(','));
      if (filters.status.length) params = params.set('estado', filters.status.join(','));
      if (filters.risk.length) params = params.set('riesgo', filters.risk.join(','));
      if (filters.flags.length) params = params.set('banderas', filters.flags.join(','));
      if (filters.lastInvoiceDateFrom) params = params.set('fInicio', filters.lastInvoiceDateFrom);
      if (filters.lastInvoiceDateTo) params = params.set('fFin', filters.lastInvoiceDateTo);
      if (filters.coverage.length) params = params.set('cobertura', filters.coverage);
      if (filters.searchTerm.length) params = params.set('terms', filters.searchTerm);
    }

    return this.http.get<ExtendedBusinessPartner[]>(`${this.apiUrl}/search/${companyId}`, {
      params,
      observe: 'response'
    }).pipe(
      tap((response) => {
        this.loadingSubject.next(false);

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'listar_paginado',
          componente_ui: 'business_partners_service',
          valor: response.body?.length || 0,
          resultado_accion: 'exitoso'
        });
      }),
      map((response) => {
        const partners = response.body || [];
        const total = parseInt(response.headers.get('X-Total-Count') || '0');
        return { partners, total };
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'listar paginado');
      })
    );
  }

  /**
    * Obtiene la información compartida con paginación y búsqueda
    * @param rutEmpresa RUT de la empresa
    * @param terms Términos de búsqueda
    * @param page Página a mostrar
    * @param len Cantidad de elementos por página
    * @returns Observable con datos y total de registros
    */
  getSharedInformationPaginated(rutEmpresa: string, terms: string, page: number, len: number): Observable<{sharedInfo: SharedInformation[], total: number}> {
    this.loadingSubject.next(true);

    let params = new HttpParams()
      .set('page', page.toString())
      .set('len', len.toString());

    if (terms && terms.trim().length > 0) {
      params = params.set('terms', terms.trim());
    }

    return this.http.get<SharedInformation[]>(`${this.apiUrl}/${rutEmpresa}/shared-information`, {
      params,
      observe: 'response'
    }).pipe(
      tap((response) => {
        this.loadingSubject.next(false);

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'listar_informacion_compartida',
          componente_ui: 'business_partners_service',
          valor: response.body?.length || 0,
          resultado_accion: 'exitoso'
        });
      }),
      map((response) => {
        const sharedInfo = response.body || [];
        const total = parseInt(response.headers.get('X-Total-Count') || '0');
        return { sharedInfo, total };
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'listar informacion compartida');
      })
    );
  }

  /**
   * Obtiene un socio de negocio por su ID
   * @param id ID del socio de negocio
   * @returns Observable con el socio
   */
  getPartnerById(id: string, companyId: string): Observable<BusinessPartner> {
    this.loadingSubject.next(true);

    return this.http.get<BusinessPartner>(`${this.apiUrl}/${id}?companyId=${companyId}`,).pipe(
      tap((partner) => {
        this.loadingSubject.next(false);

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'ver_detalle',
          componente_ui: 'business_partners_service',
          valor: id,
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'obtener detalle');
      })
    );
  }

  /**
   * Obtiene las transacciones de un socio de negocio
   * @param cliente ID del cliente (empresaId del business partner)
   * @param empresa ID de la empresa (clienteId del business partner)
   * @param tipoTx Tipo de transacción (OC)
   * @param fechaDesde Fecha de inicio para filtrar transacciones
   * @returns Observable con las transacciones
   */
  getTransacciones(cliente: string, empresa: string, tipoTx: string, fechaDesde: string): Observable<any[]> {
    this.loadingSubject.next(true);

    const params = new HttpParams()
      .set('rutProveedor', cliente)
      .set('empresa', empresa)
      .set('tipoTx', tipoTx)
      .set('fechaDesde', fechaDesde);

    return this.http.get<any[]>(`${this.apiUrl}/transacciones`, { params }).pipe(
      tap((transacciones) => {
        this.loadingSubject.next(false);

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'listar_transacciones',
          componente_ui: 'business_partners_service',
          valor: transacciones.length,
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'listar transacciones');
      })
    );
  }

  /**
   * Busca un socio de negocio por su RUT
   * @param rut RUT del socio de negocio
   * @returns Observable con el socio
   */
  getPartnerByRut(rut: string): Observable<ExtendedBusinessPartner> {
    this.loadingSubject.next(true);

    return this.http.get<ExtendedBusinessPartner>(`${this.apiUrl}/rut/${rut}`).pipe(
      tap((partner) => {
        this.loadingSubject.next(false);

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'buscar_rut',
          componente_ui: 'business_partners_service',
          valor: rut,
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'buscar por RUT');
      })
    );
  }

  /**
   * Crea un nuevo socio de negocio
   * @param partner Datos del socio a crear
   * @returns Observable con el socio creado
   */
  createPartner(partner: Partial<ExtendedBusinessPartner>): Observable<ExtendedBusinessPartner> {
    this.loadingSubject.next(true);

    return this.http.post<ExtendedBusinessPartner>(this.apiUrl, partner).pipe(
      tap((newPartner) => {
        this.loadingSubject.next(false);

        // Actualizar lista de socios
        const currentPartners = this.partnersSubject.value;
        this.partnersSubject.next([...currentPartners, newPartner]);

        // Mostrar mensaje de éxito
        this.snackBarService.displaySnackBar(
          'success',
          'Éxito',
          'Socio de negocio creado exitosamente'
        );

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'crear',
          componente_ui: 'business_partners_service',
          valor: newPartner.socioNegocioId,
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'crear');
      })
    );
  }

  /**
   * Actualiza parcialmente un socio de negocio usando PATCH
   * Solo envía los campos modificados
   * @param id ID del socio a actualizar
   * @param changes Objeto con los campos modificados
   * @returns Observable con el socio actualizado
   */
  updatePartnerPartial(id: string, companyId: string, usuarioId: string, changes: Record<string, any>): Observable<BusinessPartner> {
    this.loadingSubject.next(true);

    return this.http
      .patch<BusinessPartner>(`${this.apiUrl}/${id}?companyId=${companyId}&usuarioId=${usuarioId}`, changes)
      .pipe(
        tap((updatedPartner) => {
          this.loadingSubject.next(false);

          // Mostrar mensaje de éxito
          this.snackBarService.displaySnackBar(
            'success',
            'Éxito',
            'Socio de negocio actualizado exitosamente'
          );

          // Evento GTM
          this.gtmService.push({
            dominio: 'socios_negocio',
            accion: 'actualizar_parcial',
            componente_ui: 'business_partners_service',
            valor: id,
            resultado_accion: 'exitoso'
          });
        }),
        catchError((error) => {
          this.loadingSubject.next(false);
          return this.handleError(error, 'actualizar_parcial');
        })
      );
  }

  /**
   * Actualiza un socio de negocio enviando el objeto completo
   * @param id ID del socio a actualizar
   * @param partner Objeto completo del socio actualizado
   * @returns Observable con el socio actualizado
   */
  updatePartner(id: string, companyId: string, usuarioId: string, partner: BusinessPartner): Observable<BusinessPartner> {
    this.loadingSubject.next(true);

    return this.http
      .patch<BusinessPartner>(`${this.apiUrl}/${id}?companyId=${companyId}&usuarioId=${usuarioId}`, partner)
      .pipe(
        tap((updatedPartner) => {
          this.loadingSubject.next(false);

          // Mostrar mensaje de éxito
          this.snackBarService.displaySnackBar(
            'success',
            'Éxito',
            'Socio de negocio actualizado exitosamente'
          );

          // Evento GTM
          this.gtmService.push({
            dominio: 'socios_negocio',
            accion: 'actualizar',
            componente_ui: 'business_partners_service',
            valor: id,
            resultado_accion: 'exitoso'
          });
        }),
        catchError((error) => {
          this.loadingSubject.next(false);
          return this.handleError(error, 'actualizar');
        })
      );
  }

  /**
   * Actualiza un socio de negocio enviando el objeto completo (PUT)
   * @param id ID del socio a actualizar
   * @param partner Objeto completo del socio actualizado
   * @returns Observable con el socio actualizado
   */
  updatePartnerFull(id: string, companyId: string, usuarioId: string, partner: BusinessPartner): Observable<BusinessPartner> {
    this.loadingSubject.next(true);

    return this.http
      .put<BusinessPartner>(`${this.apiUrl}/${id}?companyId=${companyId}&usuarioId=${usuarioId}`, partner)
      .pipe(
        tap((updatedPartner) => {
          this.loadingSubject.next(false);

          // Mostrar mensaje de éxito
          this.snackBarService.displaySnackBar(
            'success',
            'Éxito',
            'Socio de negocio actualizado exitosamente'
          );

          // Evento GTM
          this.gtmService.push({
            dominio: 'socios_negocio',
            accion: 'actualizar',
            componente_ui: 'business_partners_service',
            valor: id,
            resultado_accion: 'exitoso'
          });
        }),
        catchError((error) => {
          this.loadingSubject.next(false);
          return this.handleError(error, 'actualizar');
        })
      );
  }

  addEvaluation(id: string, evaluaciones: Evaluation[]): Observable<BusinessPartner> {
      const updatePayload = {
        evaluaciones
      };

      return this.http.patch<BusinessPartner>(`${this.apiUrl}/${id}`, updatePayload).pipe(
       tap((updatedPartner) => {
          this.loadingSubject.next(false);

          // Mostrar mensaje de éxito
          this.snackBarService.displaySnackBar(
            'success',
            'Éxito',
            'Evaluación agregada exitosamente'
          );

        }),
        catchError((error) => {
          this.loadingSubject.next(false);
          return this.handleError(error, 'evaluacion');
        })
      );
    }

    addTags(id: string, usuarioId: string, tags: string[]): Observable<BusinessPartner> {
      const updatePayload = {
        usuarios: [{
          usuarioId,
          tags
        }]
      };

      return this.http.patch<BusinessPartner>(`${this.apiUrl}/${id}`, updatePayload).pipe(
       tap((updatedPartner) => {
          this.loadingSubject.next(false);

          // Mostrar mensaje de éxito
          this.snackBarService.displaySnackBar(
            'success',
            'Éxito',
            'Etiquetas actualizadas exitosamente'
          );

        }),
        catchError((error) => {
          this.loadingSubject.next(false);
          return this.handleError(error, 'actualizar etiquetas');
        })
      );
    }

  /**
   * Elimina un socio de negocio
   * @param id ID del socio a eliminar
   * @returns Observable void
   */
  deletePartner(id: string, companyId: string): Observable<void> {
    this.loadingSubject.next(true);

    return this.http.delete<void>(`${this.apiUrl}/${id}?companyId=${companyId}`).pipe(
      tap(() => {
        this.loadingSubject.next(false);
        
        // Remover de la lista
        const currentPartners = this.partnersSubject.value;
        const filteredPartners = currentPartners.filter((p) => p.socioNegocioId !== id);
        this.partnersSubject.next(filteredPartners);
        
        // Mostrar mensaje de éxito
        this.snackBarService.displaySnackBar(
          'success',
          'Éxito',
          'Socio de negocio eliminado exitosamente'
        );
        
        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'eliminar',
          componente_ui: 'business_partners_service',
          valor: id,
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'eliminar');
      })
    );
  }

  /**
   * Guarda el socio seleccionado actualmente
   * @param partner Socio de negocio seleccionado
   */
  setSelectedPartner(partner: ExtendedBusinessPartner): void {
    this.selectedPartnerSubject.next(partner);
  }

  /**
   * Obtiene el socio seleccionado actualmente
   * @returns Observable con el socio seleccionado
   */
  getSelectedPartner(): Observable<ExtendedBusinessPartner | null> {
    return this.selectedPartner$;
  }

  /**
   * Limpia el socio seleccionado
   */
  clearSelectedPartner(): void {
    this.selectedPartnerSubject.next(null);
  }


  /**
   * Obtiene los centros de gestión disponibles para una empresa
   * @param companyId ID de la empresa
   * @returns Observable con array de centros de gestión
   */
  getCentrosGestion(companyId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/centrosgestion/${companyId}`).pipe(
      map((centros) => centros),
      catchError((error) => {
        console.error('Error fetching centros gestion:', error);
        return throwError(() => new Error('Error fetching centros gestion'));
      })
    );
  }

  /**
   * Obtiene las etiquetas disponibles para una empresa
   * @param companyId ID de la empresa
   * @param usuarioId ID del usuario
   * @returns Observable con array de etiquetas
   */
  getTags(companyId: string, usuarioId: string): Observable<string[]> {
    const params = new HttpParams().set('usuarioId', usuarioId);
    return this.http.get<string[]>(`${this.apiUrl}/tags/${companyId}`, { params }).pipe(
      map((tags) => tags),
      catchError((error) => {
        console.error('Error fetching tags:', error);
        return throwError(() => new Error('Error fetching tags'));
      })
    );
  }

  /**
   * Registra un nuevo business partner con empresa y socio de negocio
   * @param data Datos del registro
   * @returns Observable con el resultado
   */
  registerBusinessPartner(data: any): Observable<any> {
    this.loadingSubject.next(true);

    return this.http.post<any>(`${this.apiUrl}/register-business-partner`, data).pipe(
      tap((response) => {
        this.loadingSubject.next(false);

        // Mostrar mensaje de éxito
        this.snackBarService.displaySnackBar(
          'success',
          'Éxito',
          'Socio de negocio registrado exitosamente'
        );

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'registrar',
          componente_ui: 'business_partners_service',
          valor: response?.socioNegocioId || 'nuevo',
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'registrar');
      })
    );
  }

  /**
   * Envía una invitación a un contacto
   * @param data Datos de la invitación
   * @returns Observable con el resultado
   */
  sendInvitation(data: any): Observable<any> {
    this.loadingSubject.next(true);

    return this.http.post<any>(`${this.apiUrl}/send-invitation`, data).pipe(
      tap((response) => {
        this.loadingSubject.next(false);

        // Mostrar mensaje de éxito
        this.snackBarService.displaySnackBar(
          'success',
          'Éxito',
          'Invitación enviada exitosamente'
        );

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'enviar_invitacion',
          componente_ui: 'business_partners_service',
          valor: data?.contactEmail || 'unknown',
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'enviar_invitacion');
      })
    );
  }

  /**
   * Envía un email de invitación a un proveedor
   * @param data Datos para el envío del email
   * @returns Observable con el resultado
   */
  sendEmailInvitation(data: any): Observable<any> {
    this.loadingSubject.next(true);

    return this.http.post<any>(`${this.apiUrl}/send-email`, data).pipe(
      tap((response) => {
        this.loadingSubject.next(false);

        // Mostrar mensaje de éxito
        this.snackBarService.displaySnackBar(
          'success',
          'Éxito',
          'Invitación enviada exitosamente'
        );

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'enviar_email_invitacion',
          componente_ui: 'business_partners_service',
          valor: data?.emailContacto || 'unknown',
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'enviar_email_invitacion');
      })
    );
  }

  /**
   * Sube un documento para un socio de negocio
   * @param socioNegocioId ID del socio de negocio
   * @param documentoId ID del documento
   * @param file Archivo a subir
   * @returns Observable con el resultado de la subida
   */
  uploadDocument(socioNegocioId: string, documentoId: string, file: File): Observable<any> {
    this.loadingSubject.next(true);
    
    const formData = new FormData();
    formData.append('documento', file);
    formData.append('documentoId', documentoId);

    return this.http.post(`${this.apiUrl}/${socioNegocioId}/documentos`, formData).pipe(
      tap((response) => {
        this.loadingSubject.next(false);
        
        this.snackBarService.displaySnackBar(
          'success',
          'Éxito',
          'Documento subido exitosamente'
        );
        
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'subir_documento',
          componente_ui: 'business_partners_service',
          valor: documentoId,
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'subir_documento');
      })
    );
  }

  /**
   * Sube un documento usando el endpoint /documentos/upload
   * Usa el mismo payload que uploadFileExternal pero sin token
   * @param formData FormData con userName, rut, rutColaborador, rutEmpresa, externalId, file
   * @returns Observable con el resultado de la subida
   */
  uploadDocumentV2(formData: FormData, propagate: boolean = true): Observable<any> {
    this.loadingSubject.next(true);

    return this.http.post(`${environment.urlApi}/documentos/upload?propagate=${propagate}`, formData).pipe(
      tap((response) => {
        this.loadingSubject.next(false);

        this.snackBarService.displaySnackBar(
          'success',
          'Éxito',
          'Documento subido exitosamente'
        );

        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'subir_documento_v2',
          componente_ui: 'business_partners_service',
          valor: 'upload',
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'subir_documento_v2');
      })
    );
  }

  /**
   * Descarga un documento por su ID
   * @param documentoId ID del documento a descargar
   * @returns Observable con el Blob del archivo
   */
  downloadDocument(documentoId: string): Observable<Blob> {
    this.loadingSubject.next(true);

    return this.http.get(
      `${environment.urlApi}/documentos/${documentoId}/download`,
      { responseType: 'blob' }
    ).pipe(
      tap(() => {
        this.loadingSubject.next(false);

        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'descargar_documento',
          componente_ui: 'business_partners_service',
          valor: documentoId,
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'descargar_documento');
      })
    );
  }

  /**
   * Maneja errores HTTP y muestra notificaciones apropiadas
   * @param error Error HTTP
   * @param action Acción que se estaba ejecutando
   * @returns Observable que propaga el error
   */
  private handleError(error: any, action: string): Observable<never> {
    let errorMessage = 'Ha ocurrido un error inesperado';
    let errorTitle = 'Error';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = error.error.message;
    } else {
      // Error del lado del servidor
      // Extraer el mensaje de error del formato: { error: string, details: { message: string, statusCode: number, timestamp: string } }
      const serverError = error.error;
      const errorDetails = serverError?.details;
      
      // El mensaje puede estar en diferentes lugares según el formato de respuesta
      let extractedMessage = '';
      if (typeof errorDetails === 'string') {
        extractedMessage = errorDetails;
      } else if (errorDetails?.message) {
        extractedMessage = errorDetails.message;
      } else if (serverError?.error) {
        extractedMessage = serverError.error;
      }

      switch (error.status) {
        case 400:
          errorTitle = 'Solicitud inválida';
          // Para el caso específico de "Ya existe un socio de negocio"
          if (extractedMessage.includes('Ya existe') || extractedMessage.includes('ya existe')) {
            errorMessage = extractedMessage || 'Ya existe un socio de negocio registrado con este RUT';
            errorTitle = 'Registro duplicado';
          } else {
            errorMessage = extractedMessage || 'Los datos enviados no son válidos';
          }
          break;
        case 401:
          errorTitle = 'No autorizado';
          errorMessage = 'No tiene permisos para realizar esta acción';
          break;
        case 404:
          errorTitle = 'No encontrado';
          errorMessage = 'El socio de negocio no existe';
          break;
        case 409:
          errorTitle = 'Conflicto';
          errorMessage = extractedMessage || 'El RUT ya está registrado';
          break;
        case 500:
          errorTitle = 'Error del servidor';
          errorMessage = 'Error interno del servidor. Intente nuevamente';
          break;
        default:
          errorMessage = extractedMessage || error.message || errorMessage;
      }
    }

    // Mostrar snackbar con el error
    this.snackBarService.displaySnackBar('error', errorTitle, errorMessage);

    // Registrar evento GTM
    this.gtmService.push({
      dominio: 'socios_negocio',
      accion: action,
      componente_ui: 'business_partners_service',
      valor: error.status,
      resultado_accion: 'fallido',
      mensaje_error: errorMessage
    });

    console.error(`Business Partner Service Error [${action}]:`, error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Obtiene estadísticas de socios de negocio
   * @param id ID de la empresa
   * @returns Observable con las estadísticas
   */
  getEstadisticas(id: string): Observable<Estadisticas> {
    this.loadingSubject.next(true);

    return this.http.get<Estadisticas>(`${this.apiUrl}/estadisticas/${id}`).pipe(
      tap((estadisticas) => {
        this.loadingSubject.next(false);

        // Evento GTM
        this.gtmService.push({
          dominio: 'socios_negocio',
          accion: 'obtener_estadisticas',
          componente_ui: 'business_partners_service',
          valor: id,
          resultado_accion: 'exitoso'
        });
      }),
      catchError((error) => {
        this.loadingSubject.next(false);
        return this.handleError(error, 'obtener_estadisticas');
      })
    );
  }
  /**
     * Solicita información de riesgo para una empresa
     * @param ID del Socio Negocio
     * @param userId ID del usuario
     * @param user Usuario que realiza la solicitud
     * @returns Observable con el resultado de la solicitud
     */
    getRiskInformationRequest(
      socioNegocioId: string,
      userId: string,
      user: string
    ): Observable<boolean> {
      return this.http
        .get<boolean>(
          `${this.apiUrl}/${socioNegocioId}/riskInformationRequest/${userId}/${user}`
        )
        .pipe(
          map((data) => data),
          catchError((error) => {
            console.error('Error requesting risk information:', error);
            return throwError(() => new Error('Error requesting risk information'));
          })
        );
    }

    /**
   * Obtiene la información de riesgo de una empresa
   * @param subcontractorIdentificationNumber RUT de la empresa
   * @returns Observable con la información de riesgo
   */
  getInformationRisk(empresaId: string): Observable<CompanyInformationRisk> {
    return this.http
      .get<CompanyInformationRisk>(
        `${this.apiUrl}/informationrisk/${empresaId}`
      )
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error fetching risk information:', error);
          return throwError(() => new Error('Error fetching risk information'));
        })
      );
  }

  /**
   * Obtiene la información de riesgo de los últimos meses de una empresa
   * @param empresaId ID de la empresa
   * @returns Observable con la información de riesgo de los últimos meses
   */
  getInformationRiskLastMonths(empresaId: string): Observable<CompanyInformationRiskLastMonths[]> {
    return this.http
      .get<CompanyInformationRiskLastMonths[]>(
        `${this.apiUrl}/informationRiskLastMonths/${empresaId}`
      )
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error fetching risk information last months:', error);
          return throwError(() => new Error('Error fetching risk information last months'));
        })
      );
  }

  /**
   * Obtiene el estado de la solicitud de riesgo
   * @param empresaId ID de la empresa
   * @param companyId ID de la compañía
   * @returns Observable con el estado de la solicitud
   */
  getRiskRequest(
    empresaId: string,
    companyId: string
  ): Observable<RiskRequest> {
    return this.http
      .get<RiskRequest>(
        `${this.apiUrl}/${empresaId}/riskRequest/${companyId}`
      )
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error fetching risk request:', error);
          return throwError(() => new Error('Error fetching risk request'));
        })
      );
  }

  /**
   * Inicia el polling para verificar el estado del request de riesgo
   * @param empresaId ID de la empresa
   * @param companyId ID de la compañía
   */
  startRiskPolling(empresaId: string, companyId: string): void {
    // Cancelar cualquier polling existente
    this.stopRiskPolling();

    // Limpiar el estado anterior
    this.riskPollingSubject.next(null);

    this.pollingSubscription = interval(this.POLLING_INTERVAL)
      .pipe(
        switchMap(() => this.getRiskRequest(empresaId, companyId))
      )
      .subscribe({
        next: (latestRequest: RiskRequest) => {
          if (latestRequest.status !== 0) {
            this.stopRiskPolling();
            const message =
              latestRequest.status === 1
                ? 'La información de riesgo ha sido actualizada correctamente'
                : 'Hubo un error actualizando la calificación de riesgo';

            // Emitir el estado actual
            this.riskPollingSubject.next({
              status: latestRequest.status,
              message,
            });

            // Mostrar snackbar directamente desde el servicio
            this.snackBarService.displaySnackBar(
              latestRequest.status === 1 ? 'success' : 'error',
              latestRequest.status === 1 ? 'Actualización exitosa' : 'Error',
              message
            );

            if (latestRequest.status === 1) {
              // Evento GTM
              this.gtmService.push({
                dominio: 'empresa',
                accion: 'resultado_riesgo',
                interaccion: 'data',
                componente_ui: 'service',
                valor: latestRequest.status,
                resultado_accion: 'exitoso',
                rut_empresa: empresaId
              });
            }

            // Si fue exitoso, esperar un momento antes de limpiar el estado
            if (latestRequest.status === 1) {
              setTimeout(() => {
                this.riskPollingSubject.next(null);
              }, 100);
            } else {
              // Si hubo error, limpiar inmediatamente
              this.riskPollingSubject.next(null);
            }
          }
        },
        error: () => {
          this.stopRiskPolling();
          const message =
            'Hubo un error actualizando la calificación de riesgo';

          // Emitir el estado de error
          this.riskPollingSubject.next({
            status: 2,
            message,
          });

          // Mostrar snackbar de error
          this.snackBarService.displaySnackBar('error', 'Error', message);

          // Limpiar el estado después de mostrar la notificación
          this.riskPollingSubject.next(null);

          // Evento GTM
          this.gtmService.push({
            dominio: 'empresa',
            accion: 'resultado_riesgo',
            interaccion: 'data',
            componente_ui: 'service',
            valor: message,
            resultado_accion: 'fallido',
            rut_empresa: empresaId
          });
        },
      });
  }

  /**
   * Detiene el polling de riesgo
   */
  stopRiskPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
    // Limpiar el estado al detener el polling
    this.riskPollingSubject.next(null);
  }

  getInformationReport(companyId: string, empresaId: string): Observable<any> {
    const url = `${this.apiUrl}/information-report/${companyId}/${empresaId}`;
    return this.http.get<any>(url);
  }
}